import type { BookedProjectDay } from "@/lib/employeeTimes";
import {
  normalizeProjectWorkflowStatus,
  PROJECT_WORKFLOW_STATUSES,
  type ProjectWorkflowStatusKey,
} from "@/lib/projects";
import { addDays, parseIso, toIso } from "./dateUtils";

export interface BookedAssignmentSpan {
  key: string;
  userLogin: string;
  auftragId: number;
  dateFrom: string;
  dateTo: string;
  title: string;
  detail: string;
  hours: number;
  /** Gebucht, obwohl der Auftrag an dem Tag nicht in der Montageplanung steht. */
  deviates: boolean;
  workflowStatus: ProjectWorkflowStatusKey;
  workflowStatusSecondary: ProjectWorkflowStatusKey | null;
  beschreibung: string;
}

export function bookedWorkflowFillClass(status: ProjectWorkflowStatusKey | string): string {
  return `mp-wf-fill--${normalizeProjectWorkflowStatus(status)}`;
}

export function bookedWorkflowEdgeClass(status: ProjectWorkflowStatusKey | string): string {
  return `mp-wf-edge--${normalizeProjectWorkflowStatus(status)}`;
}

/** Farbdarstellung gebuchter Balken: Planabgleich oder Projektzustand. */
export type BookedColorMode = "plan" | "status";

export function bookedSpanIsPaid(
  span: Pick<BookedAssignmentSpan, "workflowStatus" | "workflowStatusSecondary">,
): boolean {
  return span.workflowStatus === "bezahlt" || span.workflowStatusSecondary === "bezahlt";
}

type TaggedBookedDay = BookedProjectDay & { deviates: boolean };

function nextIso(iso: string): string {
  return toIso(addDays(parseIso(iso), 1));
}

function spanTitle(day: BookedProjectDay): string {
  const nummer = day.auftragsnummer?.trim();
  if (nummer) return nummer;
  const kunde = day.kunde?.trim();
  if (kunde) return kunde;
  return `Auftrag ${day.auftrag_id}`;
}

function spanDetail(day: BookedProjectDay): string {
  return [day.kunde?.trim(), day.ort?.trim()].filter(Boolean).join(" · ");
}

function optionalWorkflowStatus(
  value: string | null | undefined,
): ProjectWorkflowStatusKey | null {
  const key = String(value || "").trim().toLowerCase();
  if (!(PROJECT_WORKFLOW_STATUSES as readonly string[]).includes(key)) return null;
  return key as ProjectWorkflowStatusKey;
}

/** Aufeinanderfolgende Tage desselben Auftrags und derselben Plan-Abweichung werden ein Balken. */
export function groupBookedAssignmentSpans(
  days: BookedProjectDay[],
  isPlanned?: (login: string, iso: string, auftragId: number) => boolean,
): BookedAssignmentSpan[] {
  const groups = new Map<string, TaggedBookedDay[]>();
  for (const day of days) {
    const login = day.user_login.trim().toLowerCase();
    const iso = day.work_date.slice(0, 10);
    if (!login || !iso || !day.auftrag_id) continue;
    const hours = Number(day.hours);
    if (!Number.isFinite(hours) || hours <= 0) continue;
    const groupKey = `${login}|${day.auftrag_id}`;
    const list = groups.get(groupKey) ?? [];
    list.push({
      ...day,
      user_login: login,
      work_date: iso,
      hours,
      deviates: isPlanned ? !isPlanned(login, iso, day.auftrag_id) : false,
    });
    groups.set(groupKey, list);
  }

  const spans: BookedAssignmentSpan[] = [];
  for (const [groupKey, list] of groups) {
    list.sort((a, b) => a.work_date.localeCompare(b.work_date));
    let run: TaggedBookedDay[] = [];

    const flush = () => {
      const first = run[0];
      const last = run[run.length - 1];
      if (!first || !last) {
        run = [];
        return;
      }
      const hours = run.reduce((sum, day) => sum + day.hours, 0);
      const workflowStatus = normalizeProjectWorkflowStatus(first.workflow_status);
      const secondary = optionalWorkflowStatus(first.workflow_status_secondary);
      spans.push({
        key: `${groupKey}|${first.work_date}|${first.deviates ? "x" : "p"}`,
        userLogin: first.user_login,
        auftragId: first.auftrag_id,
        dateFrom: first.work_date,
        dateTo: last.work_date,
        title: spanTitle(first),
        detail: spanDetail(first),
        hours: Math.round(hours * 100) / 100,
        deviates: first.deviates,
        workflowStatus,
        workflowStatusSecondary: secondary && secondary !== workflowStatus ? secondary : null,
        beschreibung: first.beschreibung?.trim() ?? "",
      });
      run = [];
    };

    for (const day of list) {
      const prev = run[run.length - 1];
      if (prev && (nextIso(prev.work_date) !== day.work_date || prev.deviates !== day.deviates)) {
        flush();
      }
      run.push(day);
    }
    flush();
  }

  spans.sort((a, b) => a.dateFrom.localeCompare(b.dateFrom) || a.title.localeCompare(b.title));
  return spans;
}
